import { AudioLines, MoreHorizontal, Pencil, Trash2, Upload } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { UploadAudioForm } from "@/features/audio-picker";
import { ApiError } from "@/lib/api";
import { audioUsePath, audioUseText, formatBytes, formatClipLength, parseTags } from "@/lib/audio";
import { useAuth } from "@/lib/auth";
import { sameValues } from "@/lib/form";
import { useAudioLibrary, useDeleteAudio, useUpdateAudio } from "@/lib/queries";
import { atLeast, type AudioFile, type AudioUse } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { formatDateTime } from "@/lib/utils";

/**
 * The tenant's audio store (ADR-020 in platform): the sound files its lines, campaigns and voicemail
 * messages play — hold music, an entry announcement, what a lead hears when no rep is free, the
 * message a rep leaves on a voicemail.
 *
 * A file is uploaded once and chosen where it plays (`features/audio-picker.tsx`); nothing here
 * edits a setting. What a file is used by comes back with it, because that is the one thing a
 * person needs before renaming or deleting it — and the API refuses to delete a file something
 * still plays, so the menu says so first. The audio itself cannot be replaced: the switch caches
 * what it fetched, so a new recording is a new upload, chosen where it should play.
 *
 * Supervisors read it; admins upload, rename and delete.
 */
export function AudioPage() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const [search, setSearch] = React.useState("");
  const query = useDebounced(search, 250);
  // Always refetched on arrival: saving a line or a campaign changes what a file is used by.
  const { data, isPending, error, refetch } = useAudioLibrary(query, { refetchOnMount: "always" });
  const remove = useDeleteAudio();
  const [uploading, setUploading] = React.useState(false);
  const [editing, setEditing] = React.useState<AudioFile | null>(null);
  const [deleting, setDeleting] = React.useState<AudioFile | null>(null);

  const uploadButton = canManage && (
    <Button variant="default" onClick={() => setUploading(true)}>
      <Upload />
      Upload audio
    </Button>
  );

  return (
    <Page>
      <PageHeader
        title="Audio"
        description="The sound files your lines, campaigns and voicemail messages play. Upload a WAV once, then choose it where it plays."
        action={uploadButton}
      />

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            aria-label="Search the audio store"
            className="max-w-xs"
            placeholder="Search by name or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Toolbar>
        {isPending ? (
          <TableSkeleton cols={5} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load the audio store"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={AudioLines}
            title={query ? "No file matches" : "No audio yet"}
            description={
              query
                ? "Try another word."
                : "Upload hold music, an announcement or a voicemail message as a WAV file."
            }
            action={!query ? uploadButton || undefined : undefined}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Listen</TableHead>
                <TableHead>Length</TableHead>
                <TableHead>Plays in</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((f) => (
                <TableRow key={f.id}>
                  <TableCell className="max-w-72">
                    <p className="truncate text-sm font-medium text-text">{f.name}</p>
                    {f.description && (
                      <p className="line-clamp-2 text-xs text-text-muted">{f.description}</p>
                    )}
                    {f.tags.length > 0 && (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {f.tags.map((tag) => (
                          <Badge key={tag} tone="neutral" size="sm">
                            {tag}
                          </Badge>
                        ))}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <audio
                      src={f.url}
                      controls
                      preload="none"
                      aria-label={`Listen to ${f.name}`}
                      className="h-8 w-56 max-w-full"
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs tabular-nums">
                    {formatClipLength(f.duration_ms)}
                    <p className="text-text-muted">{formatBytes(f.size_bytes)}</p>
                  </TableCell>
                  <TableCell className="text-xs">
                    <Uses uses={f.used_by} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-text-muted">
                    {formatDateTime(f.created_at)}
                  </TableCell>
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${f.name}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                          <DropdownMenuItem onSelect={() => setEditing(f)}>
                            <Pencil />
                            Rename or tag
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            destructive
                            disabled={f.used_by.length > 0}
                            onSelect={() => setDeleting(f)}
                          >
                            <Trash2 />
                            Delete
                          </DropdownMenuItem>
                          {f.used_by.length > 0 && (
                            <p className="px-2 pb-1 pt-0.5 text-xs text-text-muted">
                              Still playing. Choose another sound where it plays before deleting it.
                            </p>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={uploading} onOpenChange={setUploading}>
        <DialogContent variant="drawer">
          <DialogHeader>
            <DialogTitle>Upload audio</DialogTitle>
            <DialogDescription>
              A WAV file for the audio store. Choose it afterwards on a line, a campaign or a voicemail
              message.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <UploadAudioForm
              onUploaded={(file) => {
                toast.success("Uploaded", file.name);
                setUploading(false);
              }}
              onCancel={() => setUploading(false)}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <EditAudioDialog file={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete this file?"
        description={
          <>
            <span className="font-medium">{deleting?.name}</span> is removed from the audio store for
            good. Nothing plays it now.
          </>
        }
        confirmLabel="Delete file"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("File deleted", deleting.name);
            setDeleting(null);
          } catch (e) {
            toast.error("Could not delete", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}

/** Where a file plays, each a way to the setting (`audioUsePath`): a campaign's line or its dialing
 *  tab, or the voicemail library. */
function Uses({ uses }: { uses: readonly AudioUse[] }) {
  if (uses.length === 0) return <span className="text-text-muted">Nothing yet</span>;
  return (
    <ul className="space-y-0.5">
      {uses.map((use) => {
        const to = audioUsePath(use);
        return (
          <li key={`${use.kind}:${use.id}:${use.setting}`}>
            {to ? (
              <Link className="text-accent-11 hover:underline" to={to}>
                {audioUseText(use)}
              </Link>
            ) : (
              audioUseText(use)
            )}
          </li>
        );
      })}
    </ul>
  );
}

function EditAudioDialog({ file, onClose }: { file: AudioFile | null; onClose: () => void }) {
  const toast = useToast();
  const update = useUpdateAudio();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!file) return;
    setName(file.name);
    setDescription(file.description ?? "");
    setTags(file.tags.join(", "));
    setFormError(null);
  }, [file]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) return;
    if (!name.trim()) {
      setFormError("A file needs a name.");
      return;
    }
    try {
      const saved = await update.mutateAsync({
        id: file.id,
        name: name.trim(),
        description: description.trim() || null,
        tags: parseTags(tags),
      });
      toast.success("Saved", saved.name);
      onClose();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the file.");
    }
  };

  const changed =
    file !== null &&
    !sameValues(
      { name, description, tags },
      { name: file.name, description: file.description ?? "", tags: file.tags.join(", ") },
    );

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Rename or tag</DialogTitle>
          <DialogDescription>
            The name and tags are for finding it. The audio stays the same, and so does every setting
            that plays it.
          </DialogDescription>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            <Field label="Name" htmlFor="edit-audio-name" required>
              <Input
                autoComplete="off"
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Description" htmlFor="edit-audio-description">
              <Textarea
                className="min-h-16 font-sans text-sm"
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <Field label="Tags" htmlFor="edit-audio-tags" hint="Separated by commas.">
              <Input autoComplete="off" value={tags} onChange={(e) => setTags(e.target.value)} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={update.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
